import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { loginRequest } from '@/config/authConfig'
import { MSAL_ERRORS, TOKEN_STORAGE_KEYS } from '@/constants/msalConstants'
import { msalInstance } from '@/config/msal'
import { secureStorage } from '@/libs/storage/secureStorage'

/**
 * Acquires a new token silently or redirects to login if interaction is required
 *
 * @returns The acquired token or null if no account exists
 * @throws Error if token acquisition fails
 */
export const acquireNewToken = async (): Promise<string | null> => {
  try {
    // Try to get the active account first
    let account = msalInstance.getActiveAccount()

    // If no active account, check if we have any accounts and use the first one
    if (!account) {
      const accounts = msalInstance.getAllAccounts()
      if (accounts.length > 0) {
        account = accounts[0]
        // Set this account as the active one for future use
        msalInstance.setActiveAccount(account)
      }
    }

    if (!account) {
      console.error('No active account found')

      // Check if we already have a valid token in storage despite no account
      const token = getCurrentToken('login')
      if (token) {
        return token
      }

      throw new Error(MSAL_ERRORS.NO_ACTIVE_ACCOUNT)
    }

    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account,
    })

    if (response) {
      // Store the tokens
      secureStorage.setItem(TOKEN_STORAGE_KEYS.LOGIN_TOKEN, response.idToken)
      const expiresOn = response.expiresOn?.getTime() ?? 0
      secureStorage.setItem(TOKEN_STORAGE_KEYS.LOGIN_EXPIRES, expiresOn.toString())

      return response.idToken
    }
    return null
  } catch (error) {
    console.error('Error acquiring token:', error)

    // If interaction is required, handle it by attempting popup
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const response = await msalInstance.acquireTokenPopup(loginRequest)
        if (response) {
          // Set the account as active immediately
          if (response.account) {
            msalInstance.setActiveAccount(response.account)
          }

          secureStorage.setItem(TOKEN_STORAGE_KEYS.LOGIN_TOKEN, response.idToken)
          const expiresOn = response.expiresOn?.getTime() ?? 0
          secureStorage.setItem(TOKEN_STORAGE_KEYS.LOGIN_EXPIRES, expiresOn.toString())

          return response.idToken
        }
      } catch (popupError) {
        console.error('Popup interaction failed:', popupError)
        // We'll notify the app about silent refresh failure
        window.dispatchEvent(new CustomEvent('auth:silent-refresh-failed'))
        throw popupError
      }
    }

    // Notify the app about silent refresh failure
    window.dispatchEvent(new CustomEvent('auth:silent-refresh-failed'))
    throw error
  }
}

/**
 * Acquires a token for Microsoft Graph API
 *
 * @param scopes - The scopes required for Graph API
 * @returns The acquired token or null if acquisition fails
 */
export const acquireGraphToken = async (scopes: string[]): Promise<string | null> => {
  try {
    // Try to get the active account first
    let account = msalInstance.getActiveAccount()

    // If no active account, check if we have any accounts and use the first one
    if (!account) {
      const accounts = msalInstance.getAllAccounts()
      if (accounts.length > 0) {
        account = accounts[0]
        // Set this account as the active one for future use
        msalInstance.setActiveAccount(account)
        console.log('Set active account for graph token:', account.username)
      }
    }

    if (!account) {
      // Check if we already have a valid token in storage despite no account
      const token = getCurrentToken('graph')
      if (token) {
        console.log('Using existing graph token from storage')
        return token
      }

      console.error('No active account found for graph token acquisition')
      throw new Error(MSAL_ERRORS.NO_ACTIVE_ACCOUNT)
    }

    console.log('Acquiring graph token silently with scopes:', scopes)

    // Make sure we're using the correct scopes format
    const requestScopes = Array.isArray(scopes) ? scopes : [scopes]

    const response = await msalInstance.acquireTokenSilent({
      scopes: requestScopes,
      account,
    })

    if (response) {
      console.log('Successfully acquired graph token')
      secureStorage.setItem(TOKEN_STORAGE_KEYS.GRAPH_TOKEN, response.accessToken)
      const expiresOn = response.expiresOn?.getTime() ?? 0
      secureStorage.setItem(TOKEN_STORAGE_KEYS.GRAPH_EXPIRES, expiresOn.toString())

      return response.accessToken
    }

    console.error('acquireTokenSilent returned null for graph token')
    return null
  } catch (error) {
    console.error('Error acquiring Graph token:', error)

    if (error instanceof InteractionRequiredAuthError) {
      console.log('Interaction required for graph token, attempting popup')
      try {
        // Make sure we're using the correct scopes format
        const requestScopes = Array.isArray(scopes) ? scopes : [scopes]

        const response = await msalInstance.acquireTokenPopup({
          scopes: requestScopes,
        })

        if (response) {
          console.log('Successfully acquired graph token through popup')
          // Set the account as active immediately
          if (response.account) {
            msalInstance.setActiveAccount(response.account)
          }

          secureStorage.setItem(TOKEN_STORAGE_KEYS.GRAPH_TOKEN, response.accessToken)
          const expiresOn = response.expiresOn?.getTime() ?? 0
          secureStorage.setItem(TOKEN_STORAGE_KEYS.GRAPH_EXPIRES, expiresOn.toString())

          return response.accessToken
        }

        console.error('acquireTokenPopup returned null for graph token')
        return null
      } catch (popupError) {
        console.error('Graph popup interaction failed:', popupError)
        throw popupError
      }
    }

    throw error
  }
}

/**
 * Checks if a token is expired or about to expire
 *
 * @param tokenType - The type of token to check
 * @param bufferTime - Time in milliseconds before expiration to consider token expired
 * @returns True if token is expired or will expire soon
 */
export const isTokenExpired = (tokenType: 'login' | 'graph', bufferTime = 5 * 60 * 1000): boolean => {
  const expiresKey = tokenType === 'login' ? TOKEN_STORAGE_KEYS.LOGIN_EXPIRES : TOKEN_STORAGE_KEYS.GRAPH_EXPIRES

  const tokenExpiresStr = secureStorage.getItem(expiresKey)
  if (!tokenExpiresStr) return true

  const tokenExpires = parseInt(tokenExpiresStr, 10)
  const currentTime = new Date().getTime()

  // Consider token expired 'bufferTime' before actual expiration
  return currentTime >= tokenExpires - bufferTime
}

/**
 * Logs the user out of the application
 * Clears storage and redirects to logout
 */
export const msalLogout = async (): Promise<void> => {
  // Clear all tokens from storage
  secureStorage.removeItem(TOKEN_STORAGE_KEYS.LOGIN_TOKEN)
  secureStorage.removeItem(TOKEN_STORAGE_KEYS.LOGIN_EXPIRES)
  secureStorage.removeItem(TOKEN_STORAGE_KEYS.GRAPH_TOKEN)
  secureStorage.removeItem(TOKEN_STORAGE_KEYS.GRAPH_EXPIRES)

  // Clearing zustand related keys
  localStorage.clear()

  sessionStorage.clear()

  // Notify app components about logout
  window.dispatchEvent(new CustomEvent('auth:required'))

  // Get current account and redirect to logout
  const account = msalInstance.getActiveAccount()
  await msalInstance.logoutRedirect(account ? { account } : undefined).catch((error) => {
    console.error('Logout failed:', error)
  })
}

/**
 * Gets the current active token from storage
 *
 * @param tokenType - The type of token to retrieve
 * @returns The token if it exists and is not expired, null otherwise
 */
export const getCurrentToken = (tokenType: 'login' | 'graph'): string | null => {
  const tokenKey = tokenType === 'login' ? TOKEN_STORAGE_KEYS.LOGIN_TOKEN : TOKEN_STORAGE_KEYS.GRAPH_TOKEN

  const token = secureStorage.getItem(tokenKey)

  if (token && !isTokenExpired(tokenType)) {
    return token
  }

  return null
}
